import React, { useState, useEffect } from 'react';
import { Layers, Save, X, AlertCircle, Calendar, Download, Upload, Plus, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Utility function để generate unique ID
const generateUniqueId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
    result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// Parse number safely
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : Math.max(0, num);
};

// Format number with thousand separator
const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

// Format date to YYYY-MM-DD
const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('vi-VN');
};

// Helper function để chuyển đổi date format
const convertDateFormat = (dateInput) => {
    if (!dateInput) return null;

    // Nếu là số (Excel serial date)
    if (typeof dateInput === 'number') {
        const excelEpoch = new Date(1899, 11, 30);
        const date = new Date(excelEpoch.getTime() + dateInput * 86400000);
        return formatDateForInput(date);
    }

    // Nếu là string
    if (typeof dateInput === 'string') {
        // Try DD/MM/YYYY
        const ddmmyyyyMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        // Try YYYY-MM-DD
        const yyyymmddMatch = dateInput.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (yyyymmddMatch) {
            const [, year, month, day] = yyyymmddMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }

    return null;
};

const NhapPallet = ({ onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    // State cho nhập pallet nhiều ngày
    const [palletDates, setPalletDates] = useState([]); // Array of {date, entries: [{nghiepVu, to, khoiLuong}]}
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationResults, setValidationResults] = useState(null);
    const [importPreview, setImportPreview] = useState(null);

    useEffect(() => {
        handleOpenPalletModal();
    }, []);

    // Load template nghiệp vụ Pallet từ TO_PBNS
    const loadPalletTemplate = async () => {
        try {
            const toPbnsData = await authUtils.apiRequest('TO_PBNS', 'Find', {});
            const palletCongDoan = toPbnsData.filter(item => item['NHÓM'] === 'Pallet');

            // Lấy danh sách nghiệp vụ unique với TỔ tương ứng
            const entriesMap = new Map();
            palletCongDoan.forEach(cd => {
                const nghiepVu = cd['TÊN CÔNG ĐOẠN'];
                const to = cd['TỔ'];
                if (nghiepVu && to && !entriesMap.has(nghiepVu)) {
                    entriesMap.set(nghiepVu, {
                        nghiepVu,
                        to,
                        khoiLuong: 0
                    });
                }
            });

            return Array.from(entriesMap.values());
        } catch (error) {
            console.error('Error loading pallet template:', error);
            toast.error('Lỗi khi tải danh sách nghiệp vụ');
            return [];
        }
    };

    // Mở modal nhập Pallet - Khởi tạo với 1 ngày
    const handleOpenPalletModal = async () => {
        setLoading(true);
        const template = await loadPalletTemplate();
        setPalletDates([
            {
                date: formatDateForInput(new Date()),
                entries: template
            }
        ]);
        setLoading(false);
    };

    // ==================== NHẬP PALLET FUNCTIONS ====================

    // Thêm ngày mới
    const handleAddDate = async () => {
        const template = await loadPalletTemplate();
        setPalletDates(prev => [
            ...prev,
            {
                date: formatDateForInput(new Date()),
                entries: template
            }
        ]);
    };

    // Xóa ngày
    const handleRemoveDate = (index) => {
        setPalletDates(prev => prev.filter((_, i) => i !== index));
    };

    // Cập nhật ngày
    const handleUpdateDate = (index, newDate) => {
        setPalletDates(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, date: newDate } : item
            )
        );
    };

    // Cập nhật số lượng cho một nghiệp vụ trong một ngày
    const handleUpdateKhoiLuong = (dateIndex, nghiepVu, value) => {
        setPalletDates(prev =>
            prev.map((dateItem, i) =>
                i === dateIndex
                    ? {
                        ...dateItem,
                        entries: dateItem.entries.map(entry =>
                            entry.nghiepVu === nghiepVu
                                ? { ...entry, khoiLuong: value }
                                : entry
                        )
                    }
                    : dateItem
            )
        );
    };

    // Tải template Excel
    const handleDownloadTemplate = async () => {
        try {
            const template = await loadPalletTemplate();

            if (template.length === 0) {
                toast.error('Không có dữ liệu nghiệp vụ Pallet trong TO_PBNS');
                return;
            }

            // Tạo template với tất cả nghiệp vụ
            const templateData = template.map((entry, index) => ({
                'STT': index + 1,
                'Ngày (DD/MM/YYYY)': '15/01/2025',
                'Nghiệp vụ': entry.nghiepVu,
                'Tổ': entry.to,
                'Số lượng': 0
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(templateData);

            ws['!cols'] = [
                { wch: 5 },
                { wch: 20 },
                { wch: 30 },
                { wch: 20 },
                { wch: 15 }
            ];

            XLSX.utils.book_append_sheet(wb, ws, 'Template Nhap Pallet');

            const instructions = [
                ['HƯỚNG DẪN NHẬP DỮ LIỆU PALLET'],
                [],
                ['1. Cột "Ngày": Nhập theo định dạng DD/MM/YYYY (ví dụ: 15/01/2025)'],
                ['2. Cột "Nghiệp vụ" và "Tổ": KHÔNG ĐƯỢC THAY ĐỔI'],
                ['3. Cột "Số lượng": Nhập số lượng (bỏ trống hoặc 0 nếu không có)'],
                ['4. Có thể thêm nhiều dòng cho cùng một nghiệp vụ với ngày khác nhau'],
                ['5. Sau khi điền xong, lưu file và nhập vào hệ thống'],
                [],
                ['LƯU Ý:'],
                ['- Chỉ những dòng có Số lượng > 0 mới được lưu vào hệ thống'],
                ['- Nghiệp vụ và Tổ phải khớp với danh sách trong hệ thống'],
            ];

            const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
            XLSX.utils.book_append_sheet(wb, wsInstructions, 'Hướng dẫn');

            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            saveAs(blob, `Template_Nhap_Pallet_${new Date().getTime()}.xlsx`);
            toast.success('Tải template thành công!');
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error('Lỗi khi tải template');
        }
    };

    // Import từ Excel
    const handleImportExcel = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                toast.error('File Excel không có dữ liệu');
                return;
            }

            // Load template để validate
            const template = await loadPalletTemplate();
            const validNghiepVu = new Map(template.map(e => [e.nghiepVu, e.to]));

            const errors = [];
            const warnings = [];
            const parsedDates = new Map();

            jsonData.forEach((row, index) => {
                const rowIndex = index + 2;
                const rowErrors = [];

                const ngayRaw = row['Ngày (DD/MM/YYYY)'];
                const nghiepVu = row['Nghiệp vụ'];
                const to = row['Tổ'];
                const soLuong = parseNumber(row['Số lượng']);

                const dateStr = convertDateFormat(ngayRaw);

                if (!dateStr) {
                    rowErrors.push(`Ngày không hợp lệ (${ngayRaw})`);
                }

                if (!validNghiepVu.has(nghiepVu)) {
                    rowErrors.push(`Nghiệp vụ "${nghiepVu}" không tồn tại trong hệ thống`);
                } else if (validNghiepVu.get(nghiepVu) !== to) {
                    rowErrors.push(`Tổ "${to}" không khớp với nghiệp vụ "${nghiepVu}"`);
                }

                if (soLuong < 0) {
                    rowErrors.push('Số lượng không được âm');
                }

                if (rowErrors.length > 0) {
                    errors.push(`Dòng ${rowIndex}: ${rowErrors.join('; ')}`);
                } else if (soLuong === 0) {
                    warnings.push(`Dòng ${rowIndex}: Số lượng = 0 (sẽ bỏ qua)`);
                } else {
                    if (!parsedDates.has(dateStr)) {
                        parsedDates.set(dateStr, new Map(template.map(e => [e.nghiepVu, { ...e, khoiLuong: 0 }])));
                    }

                    const dateEntries = parsedDates.get(dateStr);
                    const entry = dateEntries.get(nghiepVu);
                    if (entry) {
                        entry.khoiLuong = parseNumber(entry.khoiLuong) + soLuong;
                    }
                }
            });

            let totalRecords = 0;
            const previewData = [];

            parsedDates.forEach((entriesMap, date) => {
                const entries = Array.from(entriesMap.values()).filter(e => parseNumber(e.khoiLuong) > 0);
                if (entries.length > 0) {
                    previewData.push({ date, entries });
                    totalRecords += entries.length;
                }
            });

            setValidationResults({
                errors,
                warnings,
                isValid: errors.length === 0 && previewData.length > 0,
                totalDays: previewData.length,
                totalRecords
            });

            setImportPreview(previewData);

            if (errors.length === 0 && previewData.length > 0) {
                toast.success(`Đọc thành công ${previewData.length} ngày, ${totalRecords} bản ghi. Vui lòng kiểm tra và xác nhận!`);
            } else if (errors.length > 0) {
                toast.error(`Có ${errors.length} lỗi. Vui lòng kiểm tra và sửa file Excel!`);
            } else {
                toast.error('Không có dữ liệu hợp lệ để nhập');
            }
        } catch (error) {
            console.error('Error importing Excel:', error);
            toast.error('Lỗi khi đọc file Excel: ' + (error.message || 'Không xác định'));
            setImportPreview(null);
            setValidationResults(null);
        }

        event.target.value = '';
    };

    // Xác nhận import
    const handleConfirmImport = () => {
        if (!importPreview || !validationResults?.isValid) {
            toast.error('Dữ liệu không hợp lệ');
            return;
        }

        setPalletDates(importPreview);
        setImportPreview(null);
        setValidationResults(null);

        toast.success(`Đã nhập ${importPreview.length} ngày, ${validationResults.totalRecords} bản ghi vào form. Nhấn "Lưu" để lưu vào database!`);
    };

    // Hủy import
    const handleCancelImport = () => {
        setImportPreview(null);
        setValidationResults(null);
        toast.info('Đã hủy import');
    };

    // Lưu dữ liệu pallet
    const handleSave = async () => {
        if (isSubmitting) return;

        if (palletDates.length === 0) {
            toast.error('Vui lòng thêm ít nhất một ngày');
            return;
        }

        const invalidDate = palletDates.find(d => !d.date);
        if (invalidDate) {
            toast.error('Vui lòng chọn ngày cho tất cả các dòng');
            return;
        }

        try {
            setIsSubmitting(true);

            const allPalletRecords = [];
            let totalValidEntries = 0;

            palletDates.forEach(dateItem => {
                const validEntries = dateItem.entries.filter(entry => parseNumber(entry.khoiLuong) > 0);

                validEntries.forEach(entry => {
                    const soLuong = parseNumber(entry.khoiLuong);

                    // Bản ghi NHAPBAOBI_CT
                    allPalletRecords.push({
                        ID_CT: generateUniqueId(),
                        IDPB_REF: '',
                        IDCBB_REF: '',
                        NGÀY: dateItem.date,
                        'TÊN HÀNG': 'Pallet',
                        'NGHIỆP VỤ': entry.nghiepVu,
                        'TỔ': entry.to,
                        'KHỐI LƯỢNG (KG)': soLuong
                    });
                });

                totalValidEntries += validEntries.length;
            });

            if (allPalletRecords.length === 0) {
                toast.error('Vui lòng nhập số lượng cho ít nhất một nghiệp vụ');
                return;
            }

            // Lưu vào NHAPBAOBI_CT
            await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', { Rows: allPalletRecords });

            toast.success(`Thêm ${totalValidEntries} dòng dữ liệu Pallet cho ${palletDates.length} ngày thành công!`);
            onSuccess?.();
            onClose();

        } catch (error) {
            console.error('Error saving pallet:', error);
            toast.error('Lỗi khi lưu nhập pallet: ' + (error.message || 'Không xác định'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <Layers className="h-6 w-6 text-teal-600" />
                            Nhập dữ liệu Pallet
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                            <span className="ml-2 text-gray-600">Đang tải dữ liệu...</span>
                        </div>
                    ) : (
                        <>
                            {/* Toolbar: Template + Import Excel + Thêm ngày */}
                            <div className="flex justify-between items-center flex-wrap gap-2">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Danh sách ngày ({palletDates.length})
                                </h3>
                                <div className="flex gap-2">
                                    {/* Tải template */}
                                    <button
                                        onClick={handleDownloadTemplate}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium"
                                    >
                                        <Download className="w-4 h-4" />
                                        Tải Template
                                    </button>

                                    {/* Nhập từ Excel */}
                                    <label className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium cursor-pointer">
                                        <Upload className="w-4 h-4" />
                                        Nhập Excel
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleImportExcel}
                                            className="hidden"
                                        />
                                    </label>

                                    {/* Thêm ngày thủ công */}
                                    <button
                                        onClick={handleAddDate}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Thêm ngày
                                    </button>
                                </div>
                            </div>

                            {/* Validation Results */}
                            {validationResults && (
                                <div className="space-y-3">
                                    {validationResults.errors.length > 0 && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex items-start">
                                                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                                                <div className="text-sm text-red-800 flex-1">
                                                    <p className="font-medium mb-2">
                                                        Lỗi ({validationResults.errors.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                                        {validationResults.errors.map((err, i) => (
                                                            <li key={i}>{err}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {validationResults.warnings.length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <div className="flex items-start">
                                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                                                <div className="text-sm text-yellow-800 flex-1">
                                                    <p className="font-medium mb-2">
                                                        Cảnh báo ({validationResults.warnings.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                                        {validationResults.warnings.map((warn, i) => (
                                                            <li key={i}>{warn}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {validationResults.isValid && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                                                <Check className="w-5 h-5" />
                                                Dữ liệu hợp lệ - Xem trước
                                            </h3>
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div className="bg-white p-3 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Calendar className="w-4 h-4 text-blue-600" />
                                                        <span className="text-sm text-gray-600">Số ngày</span>
                                                    </div>
                                                    <div className="text-lg font-bold text-blue-600">
                                                        {validationResults.totalDays}
                                                    </div>
                                                </div>
                                                <div className="bg-white p-3 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Layers className="w-4 h-4 text-teal-600" />
                                                        <span className="text-sm text-gray-600">Tổng bản ghi</span>
                                                    </div>
                                                    <div className="text-lg font-bold text-teal-600">
                                                        {validationResults.totalRecords}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Preview data */}
                                            {importPreview && importPreview.length > 0 && (
                                                <div className="border border-green-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                                                    <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                                                        <h4 className="text-sm font-medium text-green-800">
                                                            Chi tiết dữ liệu import ({importPreview.length} ngày)
                                                        </h4>
                                                    </div>
                                                    <div className="p-3 space-y-3">
                                                        {importPreview.map((item, idx) => (
                                                            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="font-medium text-gray-800 flex items-center gap-2">
                                                                        <Calendar className="w-4 h-4 text-blue-600" />
                                                                        {formatDate(item.date)}
                                                                    </span>
                                                                    <span className="text-xs text-gray-600">
                                                                        {item.entries.length} bản ghi
                                                                    </span>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    {item.entries.map((entry, entryIdx) => (
                                                                        <div
                                                                            key={entryIdx}
                                                                            className="flex justify-between items-center text-xs py-1 px-2 bg-gray-50 rounded"
                                                                        >
                                                                            <span className="text-gray-700">
                                                                                {entry.nghiepVu} ({entry.to})
                                                                            </span>
                                                                            <span className="font-semibold text-teal-700">
                                                                                {formatNumber(entry.khoiLuong)}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action buttons */}
                                            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-green-200">
                                                <button
                                                    onClick={handleCancelImport}
                                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                                                >
                                                    Hủy
                                                </button>
                                                <button
                                                    onClick={handleConfirmImport}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Xác nhận nhập ({validationResults.totalDays} ngày, {validationResults.totalRecords} bản ghi)
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Danh sách các ngày */}
                            {palletDates.map((dateItem, dateIndex) => (
                                <div key={dateIndex} className="border-2 border-gray-300 rounded-lg p-4 space-y-3 bg-gray-50">
                                    {/* Header của mỗi ngày */}
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ngày {dateIndex + 1} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                </div>
                                                <input
                                                    type="date"
                                                    value={dateItem.date}
                                                    onChange={(e) => handleUpdateDate(dateIndex, e.target.value)}
                                                    className="pl-10 p-2.5 border border-gray-300 rounded-lg w-full focus:ring-teal-500 focus:border-teal-500"
                                                />
                                            </div>
                                        </div>
                                        {palletDates.length > 1 && (
                                            <button
                                                onClick={() => handleRemoveDate(dateIndex)}
                                                className="mt-6 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm"
                                            >
                                                <X className="w-4 h-4" />
                                                Xóa
                                            </button>
                                        )}
                                    </div>

                                    {/* Bảng nhập liệu cho ngày này */}
                                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                                            <thead className="bg-teal-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-teal-800 uppercase tracking-wider">
                                                        STT
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-teal-800 uppercase tracking-wider">
                                                        Nghiệp vụ
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-teal-800 uppercase tracking-wider">
                                                        Tổ
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-teal-800 uppercase tracking-wider">
                                                        Số lượng
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {dateItem.entries.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={4} className="px-3 py-4 text-center text-gray-500 italic text-xs">
                                                            Đang tải danh sách nghiệp vụ...
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    dateItem.entries.map((entry, entryIndex) => (
                                                        <tr key={entry.nghiepVu} className="hover:bg-gray-50">
                                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                                {entryIndex + 1}
                                                            </td>
                                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                                                {entry.nghiepVu}
                                                            </td>
                                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                                {entry.to}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={entry.khoiLuong}
                                                                    onChange={(e) => handleUpdateKhoiLuong(dateIndex, entry.nghiepVu, e.target.value)}
                                                                    className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-xs"
                                                                    placeholder="0"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Thống kê cho ngày này */}
                                    {dateItem.entries.length > 0 && (
                                        <div className="bg-white border border-gray-200 rounded p-2 text-xs">
                                            <span className="text-gray-700">
                                                <span className="font-semibold">Đã nhập:</span>{' '}
                                                <span className="text-teal-600 font-bold">
                                                    {dateItem.entries.filter(e => parseNumber(e.khoiLuong) > 0).length}
                                                </span>
                                                {' / '}
                                                {dateItem.entries.length}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Thống kê tổng */}
                            {palletDates.length > 0 && (
                                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 text-sm">
                                    <p className="text-blue-900 font-semibold">
                                        <span>Tổng số ngày: {palletDates.length}</span>
                                        {' | '}
                                        <span>Tổng số dòng sẽ lưu: </span>
                                        <span className="text-blue-700 font-bold">
                                            {palletDates.reduce((total, dateItem) =>
                                                total + dateItem.entries.filter(e => parseNumber(e.khoiLuong) > 0).length, 0
                                            )}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                    >
                        Đóng
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSubmitting || loading}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                        {isSubmitting || loading ? (
                            <>
                                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                Đang lưu...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Lưu dữ liệu ({palletDates.reduce((total, dateItem) =>
                                    total + dateItem.entries.filter(e => parseNumber(e.khoiLuong) > 0).length, 0
                                )} dòng)
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NhapPallet;
